import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthService from '../../services/authService';
import { logger } from '../../utils/logger';
import config from '../../config/api.config';

const MagicTokenHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasProcessed, setHasProcessed] = useState(false);

  useEffect(() => {
    // Check if we've already processed this token
    const processedTokens = sessionStorage.getItem('processed_tokens');
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    
    if (processedTokens && token && processedTokens.includes(token)) {
      logger.info('Token already processed, waiting for redirect...', 'MagicTokenHandler');
      return;
    }

    const processMagicToken = async () => {
      // Prevent multiple executions
      if (hasProcessed) {
        logger.info('Already processing token, skipping...', 'MagicTokenHandler');
        return;
      }
      
      setHasProcessed(true);
      
      logger.info('Processing magic token', 'MagicTokenHandler');
      
      // Get URL parameters
      const urlBrandId = searchParams.get('brandId');
      const urlBranchId = searchParams.get('branchId');
      
      // Check for existing saved selections
      const savedBrandId = AuthService.getSavedBrandId();
      const savedBranchId = AuthService.getSavedBranchId();
      
      // Use URL parameters if provided, otherwise use saved values
      const brandId = urlBrandId || (savedBrandId ? savedBrandId.toString() : null);
      const branchId = urlBranchId || (savedBranchId ? savedBranchId.toString() : null);
      
      // Save to localStorage if URL parameters are provided
      if (urlBrandId && urlBrandId !== '0') {
        AuthService.saveBrandSelection(parseInt(urlBrandId, 10));
      }
      if (urlBranchId && urlBranchId !== '0') {
        AuthService.saveBranchSelection(parseInt(urlBranchId, 10));
      }
      
      logger.debug('Token info', 'MagicTokenHandler', { token: token ? token.substring(0, 10) + '...' : 'null' });
      logger.debug('Brand selection', 'MagicTokenHandler', { urlBrandId, savedBrandId, using: brandId }); 
      logger.debug('Branch selection', 'MagicTokenHandler', { urlBranchId, savedBranchId, using: branchId });

      if (!token) {
        logger.info('No token found, redirecting to home', 'MagicTokenHandler');
        navigate('/');
        return;
      }

      // Mark token as processed
      const processed = sessionStorage.getItem('processed_tokens') || '';
      sessionStorage.setItem('processed_tokens', processed + ',' + token);

      try {
        // Call the auth service directly
        // Pass brandId and branchId as they are (null values will be handled by the service)
        const response = await AuthService.validateMagicToken(token, brandId || undefined, branchId || undefined);
        logger.debug('Token validation response', 'MagicTokenHandler', response);
        
        // Ensure tokens are saved before redirect
        const savedToken = localStorage.getItem('insight_auth_token');
        logger.debug('Token saved to localStorage', 'MagicTokenHandler', { saved: !!savedToken });
        
        // Small delay to ensure localStorage is synced
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Instead of using window.location.href which can cause issues,
        // use React Router navigation after ensuring auth is saved
        logger.info('Authentication successful, navigating to dashboard...', 'MagicTokenHandler');
        
        // Clear any pending redirects in sessionStorage
        sessionStorage.removeItem('pending_redirect');
        
        // Use replace to ensure back button doesn't return to magic token URL
        navigate(response.redirect_url || '/dashboard/main', { replace: true });
      } catch (error: any) {
        logger.error('Token validation failed', 'MagicTokenHandler', error);
        
        // Check if it's a 400 error (token already used)
        if (error.response?.status === 400) {
          // Token was already used, check if we have auth
          const authToken = localStorage.getItem('insight_auth_token');
          if (authToken) {
            // We have auth, redirect to dashboard using React Router
            logger.info('Token already used but user is authenticated, redirecting to dashboard', 'MagicTokenHandler');
            navigate('/dashboard/main', { replace: true });
            return;
          }
        }
        
        setError('احراز هویت با خطا مواجه شد');
        setIsProcessing(false);
        
        // Redirect to home after 3 seconds
        setTimeout(() => {
          navigate(config.STAGE_NO_AUTH ? '/dashboard/main' : '/', { replace: true });
        }, 3000);
      }
    };

    if (!hasProcessed && token) {
      processMagicToken();
    }
  }, [location, navigate, hasProcessed]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 text-red-600">{error}</h2>
          <p className="text-gray-600">در حال بازگشت به صفحه اصلی…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-4">در حال احراز هویت…</h2>
        <div className="animate-loading-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
      </div>
    </div>
  );
};

export default MagicTokenHandler;
